import { z } from 'zod';

import { entitlementsSchema } from '@/lib/entitlements';

const tenantRowSchema = z.object({
  slug: z.string(),
  name: z.string(),
  city: z.string(),
  customDomain: z.string().nullable(),
  status: z.string(),
  subscriptionTier: z.string(),
  billingStatus: z.string(),
  trialEndsAt: z.string().nullable(),
  subscriptionEndsAt: z.string().nullable(),
  customDomainAllowed: z.boolean(),
  vehicleLimit: z.number().int().nullable(),
  staffLimit: z.number().int().nullable(),
  vehicles: z.number().int().nullable(),
  bookings: z.number().int().nullable(),
  openBookings: z.number().int().nullable(),
  bookedValue: z.string().nullable(),
  customers: z.number().int().nullable(),
  contracts: z.number().int().nullable(),
  activeRentalsToday: z.number().int().nullable(),
  storefrontPublishedAt: z.string().nullable(),
  design: z.string().nullable(),
  staffAccounts: z.number().int().nullable(),
  nearCap: z.boolean(),
  error: z.string().nullable(),
  latitude: z.number().nullable().optional().default(null),
  longitude: z.number().nullable().optional().default(null),
});

const overviewSchema = z.object({
  counts: z.object({
    agencies: z.number().int(),
    active: z.number().int(),
    paused: z.number().int(),
    unreachable: z.number().int(),
    nearCap: z.number().int(),
  }),
  tenants: z.array(tenantRowSchema),
});

const tenantProfileSchema = z.object({
  name: z.string(),
  city: z.string(),
  address: z.string(),
  phone: z.string(),
  whatsapp: z.string(),
  email: z.string(),
  ice: z.string(),
  rc: z.string(),
});

const tenantDetailSchema = z.object({
  slug: z.string(),
  latitude: z.number().nullable().optional().default(null),
  longitude: z.number().nullable().optional().default(null),
  profile: tenantProfileSchema.nullable(),
  staff: z.array(z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.string(),
    active: z.boolean(),
  })),
  entitlements: entitlementsSchema.nullable().optional().default(null),
  error: z.string().nullable(),
});

export type TenantDetail = z.infer<typeof tenantDetailSchema>;

export function parseTenantDetail(payload: unknown): TenantDetail {
  const parsed = tenantDetailSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The agency detail could not be read.');
  return parsed.data;
}

export type PlatformSummary = z.infer<typeof overviewSchema>;
export type PlatformTenantRow = z.infer<typeof tenantRowSchema>;

export function parsePlatformSummary(payload: unknown): PlatformSummary {
  const parsed = overviewSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The platform overview could not be read.');
  return parsed.data;
}

export type TenantHealthFilter = 'all' | 'open' | 'unreachable';

export type TenantDirectoryQuery = {
  text: string;
  status: string;
  plan: string;
  health: TenantHealthFilter;
};

export function filterTenantDirectory(rows: PlatformTenantRow[], query: TenantDirectoryQuery): PlatformTenantRow[] {
  const needle = query.text.trim().toLowerCase();
  return rows.filter((row) => {
    if (query.status !== 'all' && row.status !== query.status) return false;
    if (query.plan !== 'all' && row.subscriptionTier !== query.plan) return false;
    if (!matchesHealth(row, query.health)) return false;
    if (needle === '') return true;
    return [row.name, row.slug, row.city].some((value) => value.toLowerCase().includes(needle));
  });
}

function matchesHealth(row: PlatformTenantRow, health: TenantHealthFilter): boolean {
  switch (health) {
    case 'all':
      return true;
    case 'open':
      return row.error === null;
    case 'unreachable':
      return row.error !== null;
    default: {
      const unknown: never = health;
      return unknown;
    }
  }
}

const healthSchema = z.object({
  status: z.enum(['connected', 'unreachable']),
  latencyMs: z.number().int().nullable(),
  tables: z.number().int().nullable(),
  error: z.string().nullable(),
});

export type TenantHealth = z.infer<typeof healthSchema>;

export function parseTenantHealth(payload: unknown): TenantHealth {
  const parsed = healthSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The database check could not be read.');
  return parsed.data;
}

const themeSchema = z.object({
  design: z.enum(['classic', 'editorial', 'roadtrip']),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  logoUrl: z.string(),
  faviconUrl: z.string(),
  publishedAt: z.string().nullable(),
  canRevert: z.boolean(),
});

export type TenantTheme = z.infer<typeof themeSchema>;

export function parseTenantTheme(payload: unknown): TenantTheme {
  const parsed = themeSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The storefront theme could not be read.');
  return parsed.data;
}

/** Matches the API: a null limit is unlimited, and 80% or more is near the cap. */
export function approachingCap(limit: number | null, used: number | null): boolean {
  if (limit === null || used === null) return false;
  if (limit === 0) return true;
  return used * 5 >= limit * 4;
}
